import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import API from '../api';
import { BrandsPageSkeleton } from '../components/skeleton';

const PAGE_SIZE = 50;

const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [after, setAfter] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Carica brand (paginato)
  const fetchBrands = useCallback(async (reset = false, searchValue = search) => {
    setLoading(true);
    setError(null);
    try {
      const params = { size: PAGE_SIZE };
      if (searchValue) params.q = searchValue;
      if (!reset && after) params.after = JSON.stringify(after);
      const res = await API.get('/brands/search', { params });
      const newBrands = res.data.brands || [];
      setBrands(prev => reset ? newBrands : [...prev, ...newBrands]);
      setAfter(res.data.nextAfter || null);
      setHasMore(!!res.data.nextAfter && newBrands.length > 0);
    } catch (err) {
      setError('Errore nel caricamento dei brand');
    } finally {
      setLoading(false);
      setIsFirstLoad(false);
    }
  }, [after, search]);

  // Primo caricamento o ricerca
  useEffect(() => {
    setBrands([]);
    setAfter(null);
    setHasMore(true);
    setIsFirstLoad(true);
    fetchBrands(true, search);
    // eslint-disable-next-line
  }, [search]);

  // Infinite scroll
  const lastBrandRef = useCallback(node => {
    if (loading || !hasMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new window.IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        fetchBrands();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore, fetchBrands]);

  // Raggruppa i brand per lettera iniziale, numeri sotto '0-9'
  const brandsByLetter = brands.reduce((acc, brand) => {
    const firstChar = brand[0]?.toUpperCase() || '';
    const letter = /[0-9]/.test(firstChar) ? '0-9' : firstChar;
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(brand);
    return acc;
  }, {});
  const sortedLetters = Object.keys(brandsByLetter).sort((a, b) => {
    if (a === '0-9') return -1;
    if (b === '0-9') return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="main-content-container brands-page">
      <h1 className="brands-title">Tutti i Brand</h1>
      <div className="brands-searchbar-wrapper">
        <input
          className="brands-searchbar"
          type="text"
          placeholder="Cerca un brand..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Cerca brand"
        />
      </div>
      {isFirstLoad && loading && <BrandsPageSkeleton />}
      {error && (
        <div className="brands-error">
          {error}
        </div>
      )}
      {!isFirstLoad && brands.length === 0 && !loading && !error && (
        <div className="brands-empty">Nessun brand trovato.</div>
      )}
      <div className="brands-list">
        {sortedLetters.map(letter => (
          <div key={letter} className="brands-letter-section">
            <div className="brands-letter-header">{letter}</div>
            <div className="brands-grid">
              {brandsByLetter[letter].map((brand, idx, arr) => {
                const isLast = idx === arr.length - 1 && letter === sortedLetters[sortedLetters.length - 1];
                return (
                  <Link
                    key={brand}
                    to={`/brands/${encodeURIComponent(brand)}`}
                    className="brand-link"
                    ref={isLast ? lastBrandRef : undefined}
                  >
                    {brand}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {loading && !isFirstLoad && (
        <div className="brands-loading">
          <div className="brands-spinner" />
        </div>
      )}
    </div>
  );
};

export default BrandsPage; 